import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Select,
  DatePicker,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Space,
  Button,
  Divider,
  Tag,
  message,
  Spin,
} from "antd";
import {
  BankOutlined,
  PrinterOutlined,
  FileExcelOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const ZAMBIAN_COLORS = {
  green: "#078930",
  red: "#EF3340",
  orange: "#FFB81C",
};

const formatCurrency = (value) =>
  `ZMW ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const AccountStatement = () => {
  const { company, accounts } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dateRange, setDateRange] = useState([
    moment().startOf("month"),
    moment(),
  ]);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    openingBalance: 0,
    totalDebits: 0,
    totalCredits: 0,
    closingBalance: 0,
  });

  useEffect(() => {
    if (accounts && accounts.length > 0 && !selectedAccount) {
      // Auto-select first active account
      const firstActive = accounts.find((acc) => acc.status === "active");
      if (firstActive) {
        setSelectedAccount(firstActive.id);
      }
    }
  }, [accounts]);

  useEffect(() => {
    if (selectedAccount) {
      fetchAccountStatement();
    }
  }, [selectedAccount, dateRange]);

  const fetchAccountStatement = async () => {
    if (!selectedAccount || !company?.id || !token) return;

    setLoading(true);
    try {
      const [startDate, endDate] = dateRange;

      // Fetch transactions for this account
      // Note: If your transactions don't have account fields, we'll fetch ALL transactions
      // and filter them in the categorizeTransaction function
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/transactions",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: {
              company: company.id,
              transaction_status: "Completed",
              transaction_date: {
                _between: [
                  startDate.format("YYYY-MM-DD"),
                  endDate.format("YYYY-MM-DD"),
                ],
              },
              // Only filter by account if the fields exist, otherwise get all
              // This is handled by the _or clause with _nnull (not null) checks
            },
            fields:
              "id,transaction_date,transaction_type,amount,notes,reference_number,payment_method",
            sort: "transaction_date",
            limit: -1,
          },
        }
      );

      const txns = response.data.data || [];

      // Get the account's current balance to use as baseline
      const accountResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/accounts/${selectedAccount}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const currentAccountBalance = parseFloat(accountResponse.data.data.balance || 0);

      // Calculate what happened during the selected period
      let periodDebits = 0;
      let periodCredits = 0;
      txns.forEach((txn) => {
        const { debit, credit } = categorizeTransaction(txn);
        periodDebits += debit;
        periodCredits += credit;
      });

      // Opening balance = current balance - period net change
      const periodNetChange = periodDebits - periodCredits;
      const openingBalance = currentAccountBalance - periodNetChange;

      // Process transactions with running balance
      let runningBalance = openingBalance;
      let totalDebits = 0;
      let totalCredits = 0;

      const processedTransactions = txns.map((txn) => {
        const { debit, credit, description } = categorizeTransaction(txn);

        totalDebits += debit;
        totalCredits += credit;
        runningBalance += debit - credit;

        return {
          ...txn,
          debit,
          credit,
          balance: runningBalance,
          description,
        };
      });

      setTransactions(processedTransactions);
      setSummary({
        openingBalance,
        totalDebits,
        totalCredits,
        closingBalance: runningBalance,
      });
    } catch (error) {
      console.error("Error fetching account statement:", error);
      message.error("Failed to load account statement");
    } finally {
      setLoading(false);
    }
  };

  // Categorize transaction as debit or credit from the perspective of selected account
  // Note: Since transactions don't have account links, we categorize based purely on transaction type
  const categorizeTransaction = (txn) => {
    const amount = parseFloat(txn.amount || 0);
    let debit = 0;
    let credit = 0;
    let description = txn.notes || txn.transaction_type;

    // Categorize based on transaction type (all transactions shown for all accounts)
    switch (txn.transaction_type) {
      case "Loan Disbursement":
        // Money going out (Credit to Cash account)
        credit = amount;
        description = `Loan disbursement - Ref: ${txn.reference_number || "N/A"}`;
        break;

      case "Loan Repayment":
        // Money coming in (Debit to Cash account)
        debit = amount;
        description = `Loan repayment received - Ref: ${txn.reference_number || "N/A"}`;
        break;

      case "Interest Payment":
        // Money coming in (Debit to Cash account)
        debit = amount;
        description = `Interest payment received`;
        break;

      case "Admin Fees":
      case "GPS Fee Payment":
      case "Penalty Fee":
        // Money coming in (Debit to Cash account)
        debit = amount;
        description = `${txn.transaction_type} received`;
        break;

      case "Float Addition":
        // Money coming in (Debit to Cash account)
        debit = amount;
        description = "Float addition";
        break;

      case "Operational Cost":
      case "Transfer Fees":
        // Money going out (Credit to Cash account)
        credit = amount;
        description = `${txn.transaction_type} paid`;
        break;

      case "Account Transfer":
        // For account transfers, we can't determine direction without account fields
        // So we'll just show as note
        description = `Account transfer - Ref: ${txn.reference_number || "N/A"}`;
        break;

      default:
        // For unknown types, show as note
        description = txn.notes || `${txn.transaction_type} - Ref: ${txn.reference_number || "N/A"}`;
    }

    return { debit, credit, description };
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "transaction_date",
      key: "date",
      width: 110,
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Reference",
      dataIndex: "reference_number",
      key: "reference",
      width: 150,
      render: (ref) => <Text code>{ref || "N/A"}</Text>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Debit",
      dataIndex: "debit",
      key: "debit",
      align: "right",
      width: 140,
      render: (value) => (
        <Text strong style={{ color: value > 0 ? ZAMBIAN_COLORS.green : undefined }}>
          {value > 0 ? formatCurrency(value) : "-"}
        </Text>
      ),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      key: "credit",
      align: "right",
      width: 140,
      render: (value) => (
        <Text strong style={{ color: value > 0 ? ZAMBIAN_COLORS.red : undefined }}>
          {value > 0 ? formatCurrency(value) : "-"}
        </Text>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      width: 150,
      fixed: "right",
      render: (value) => (
        <Text strong style={{ fontSize: 14 }}>
          {formatCurrency(value)}
        </Text>
      ),
    },
  ];

  const selectedAccountInfo = accounts?.find((acc) => acc.id === selectedAccount);

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    message.info("Export to Excel feature coming soon!");
  };

  return (
    <div style={{ padding: 24 }} className="account-statement-page">
      <Card
        bordered={false}
        style={{
          borderTop: `4px solid ${ZAMBIAN_COLORS.green}`,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.09)",
        }}
      >
        {/* Header */}
        <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ color: ZAMBIAN_COLORS.green, marginBottom: 0 }}>
              <BankOutlined /> Account Statement
            </Title>
            <Text type="secondary">
              Detailed transaction history with debits, credits, and running balance
            </Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<FileExcelOutlined />} onClick={handleExport}>
                Export
              </Button>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                style={{
                  backgroundColor: ZAMBIAN_COLORS.green,
                  borderColor: ZAMBIAN_COLORS.green,
                }}
              >
                Print
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12}>
            <Text strong>Select Account</Text>
            <Select
              value={selectedAccount}
              onChange={setSelectedAccount}
              style={{ width: "100%", marginTop: 8 }}
              placeholder="Select an account"
            >
              {accounts
                ?.filter((acc) => acc.status === "active")
                .map((account) => (
                  <Option key={account.id} value={account.id}>
                    <Space>
                      <BankOutlined />
                      {account.name}
                      <Text type="secondary">
                        (Balance: {formatCurrency(account.balance)})
                      </Text>
                    </Space>
                  </Option>
                ))}
            </Select>
          </Col>

          <Col xs={24} md={12}>
            <Text strong>Date Range</Text>
            <RangePicker
              value={dateRange}
              onChange={(range) => range && setDateRange(range)}
              style={{ width: "100%", marginTop: 8 }}
              format="DD MMM YYYY"
            />
          </Col>
        </Row>

        <Divider />

        {selectedAccountInfo && (
          <>
            {/* Account Info */}
            <Card
              style={{ backgroundColor: "#fafafa", marginBottom: 16 }}
              bodyStyle={{ padding: 16 }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Text strong>Account Name:</Text>
                  <br />
                  <Text style={{ fontSize: 16 }}>{selectedAccountInfo.name}</Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong>Account Code:</Text>
                  <br />
                  <Text style={{ fontSize: 16 }}>
                    {selectedAccountInfo.account_code}
                  </Text>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 12 }}>
                <Col xs={24} sm={12}>
                  <Text strong>Statement Period:</Text>
                  <br />
                  <Text style={{ fontSize: 16 }}>
                    {dateRange[0].format("DD MMM YYYY")} to{" "}
                    {dateRange[1].format("DD MMM YYYY")}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong>Status:</Text>
                  <br />
                  <Tag
                    color={
                      selectedAccountInfo.status === "active"
                        ? ZAMBIAN_COLORS.green
                        : ZAMBIAN_COLORS.red
                    }
                    style={{ marginTop: 4 }}
                  >
                    {selectedAccountInfo.status || "Active"}
                  </Tag>
                </Col>
              </Row>
            </Card>

            {/* Summary Statistics */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <Card>
                  <Statistic
                    title="Opening Balance"
                    value={summary.openingBalance}
                    precision={2}
                    prefix="ZMW"
                    valueStyle={{ fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card>
                  <Statistic
                    title="Total Debits"
                    value={summary.totalDebits}
                    precision={2}
                    valueStyle={{ color: ZAMBIAN_COLORS.green, fontSize: 18 }}
                    prefix={<ArrowUpOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card>
                  <Statistic
                    title="Total Credits"
                    value={summary.totalCredits}
                    precision={2}
                    valueStyle={{ color: ZAMBIAN_COLORS.red, fontSize: 18 }}
                    prefix={<ArrowDownOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card>
                  <Statistic
                    title="Closing Balance"
                    value={summary.closingBalance}
                    precision={2}
                    prefix="ZMW"
                    valueStyle={{
                      color:
                        summary.closingBalance >= summary.openingBalance
                          ? ZAMBIAN_COLORS.green
                          : ZAMBIAN_COLORS.red,
                      fontSize: 18,
                      fontWeight: "bold",
                    }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Transactions Table */}
            <Spin spinning={loading}>
              <Table
                dataSource={transactions}
                columns={columns}
                rowKey="id"
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} transactions`,
                }}
                scroll={{ x: "max-content" }}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: "#e6f7ff" }}>
                    <Table.Summary.Cell colSpan={3}>
                      <Text strong style={{ fontSize: 14 }}>
                        TOTAL
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ fontSize: 14, color: ZAMBIAN_COLORS.green }}>
                        {formatCurrency(summary.totalDebits)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ fontSize: 14, color: ZAMBIAN_COLORS.red }}>
                        {formatCurrency(summary.totalCredits)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ fontSize: 14 }}>
                        Net: {formatCurrency(summary.totalDebits - summary.totalCredits)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />

              {!loading && transactions.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Text type="secondary">
                    No transactions found for this account in the selected period.
                  </Text>
                </div>
              )}
            </Spin>

            {/* Explanation */}
            <Card style={{ marginTop: 16, backgroundColor: "#f6ffed" }}>
              <Title level={5}>Understanding Your Statement</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <ul style={{ marginBottom: 0 }}>
                    <li>
                      <Text strong style={{ color: ZAMBIAN_COLORS.green }}>
                        Debit
                      </Text>{" "}
                      - Money coming into this account
                    </li>
                    <li>
                      <Text strong style={{ color: ZAMBIAN_COLORS.red }}>
                        Credit
                      </Text>{" "}
                      - Money going out of this account
                    </li>
                  </ul>
                </Col>
                <Col xs={24} md={12}>
                  <ul style={{ marginBottom: 0 }}>
                    <li>
                      <Text strong>Balance</Text> - Running balance after each
                      transaction
                    </li>
                    <li>
                      <Text strong>Net Change</Text> ={" "}
                      {formatCurrency(summary.totalDebits - summary.totalCredits)}
                    </li>
                  </ul>
                </Col>
              </Row>
            </Card>
          </>
        )}
      </Card>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .ant-btn,
          .ant-select,
          .ant-picker {
            display: none !important;
          }
          .account-statement-page {
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AccountStatement;
